import { formatDateString } from "@/v2-common/utils/utils.date"
import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import {
  presetBadgeVariant,
  privacyPolicyLabel,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import type { PrivacySummary } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyPresetBadgeProps = Omit<
  BadgeProps,
  "variant" | "tooltip" | "children"
> & {
  /**
   * Null or undefined renders nothing: the summary is a second-database read
   * that may be missing on a row whose other fields are fine, and a pill
   * saying "unknown" would be noise where silence is honest.
   */
  summary: PrivacySummary | null | undefined
}

// THE pill for a team's personal-data policy: the preset the effective
// policy matches (Default, All hidden, All open) or Custom, toned by how
// much the preset opens up. One component for the teams list, the team
// header and the editor's status row, so the same team reads the same
// everywhere — and because the summary is one cache record under all three,
// a change saved in the editor moves every pill at once.
function TenantPrivacyPresetBadge(props: TenantPrivacyPresetBadgeProps) {
  const { summary, ...badgeProps } = props

  if (!summary) return null

  const preset = summary.MatchesPreset
  const label = privacyPolicyLabel(summary)

  return (
    <Badge
      variant={presetBadgeVariant(preset)}
      tooltip={
        <div className="flex max-w-xs flex-col gap-1">
          <p className="font-medium">{label}</p>
          <p className="text-muted-foreground">
            {preset?.Description ??
              "Matches no preset — the per-source policy is in the team's Access Control tab."}
          </p>
          <p className="text-muted-foreground">
            {summary.PolicyRowCount === 0
              ? "No policy has been set: every personal-data field is hidden."
              : `${summary.PolicyRowCount} source${summary.PolicyRowCount === 1 ? "" : "s"} configured` +
                (summary.LastUpdatedAt
                  ? ` · last change ${formatDateString(summary.LastUpdatedAt)}`
                  : "")}
          </p>
        </div>
      }
      {...badgeProps}
    >
      {label}
    </Badge>
  )
}

export default TenantPrivacyPresetBadge
